// Encode an AudioBuffer to a 16-bit PCM WAV Blob.
//
// Used to turn an OfflineAudioContext-rendered mixdown (see mix-renderer.ts)
// into something a plain <audio> element can play — the one thing iOS keeps
// alive in the background. Interleaved little-endian PCM, 44-byte canonical
// header. No dependencies, no base64.

/** Little-endian 16-bit PCM WAV. `audio/wav` Blob, playable by HTMLAudioElement. */
export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataBytes = numFrames * blockAlign;

  const ab = new ArrayBuffer(44 + dataBytes);
  const dv = new DataView(ab);
  const writeStr = (off: number, s: string): void => {
    for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  dv.setUint32(4, 36 + dataBytes, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  dv.setUint32(16, 16, true); // fmt chunk size (PCM)
  dv.setUint16(20, 1, true); // audio format = PCM
  dv.setUint16(22, numChannels, true);
  dv.setUint32(24, sampleRate, true);
  dv.setUint32(28, sampleRate * blockAlign, true); // byte rate
  dv.setUint16(32, blockAlign, true);
  dv.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  dv.setUint32(40, dataBytes, true);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) channels.push(buffer.getChannelData(c));

  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numChannels; c++) {
      let s = channels[c][i];
      // Clamp to [-1, 1], then scale with the asymmetric int16 range
      // (+1 → 32767, -1 → -32768).
      s = s < -1 ? -1 : s > 1 ? 1 : s;
      dv.setInt16(offset, (s < 0 ? s * 0x8000 : s * 0x7fff) | 0, true);
      offset += 2;
    }
  }

  return new Blob([ab], { type: 'audio/wav' });
}
